import { cond, find, flow, has, identity, omit, T } from 'lodash/fp';
import DonorTypes from '@/constants/constituents/DonorTypes';
import ContributionType from '@/constants/ContributionType';
import { DONATION_TYPE_OPTIONS } from '@/lib/configs/selectOptionsConfig';
import { API_DATE_FORMAT } from '@/lib/constants/common';
import { formatCurrency, formatCurrencyInputToCents } from '@/utils/currency';
import { dateStringToDate, formatDateObject } from '@/utils/dates';

export function canHaveEarnedRevenue(contributionType) {
  return [
    ContributionType.GENERAL,
    ContributionType.RECURRING,
    ContributionType.RESTRICTED,
  ].includes(contributionType);
}

export function formatConstituent({ donor, ...form }) {
  return {
    ...form,
    ...(donor.icon && {
      donorAvatarUrl: donor.icon,
    }),
    constituentType: donor.constituentType,
    constituentId: donor.value,
    constituentDisplayName: donor.text,
  };
}

export const DEFAULT_VALUES = {
  attachToPledge: false,
  attachments: [],
  campaign: null,
  dedicatedTo: null,
  dedication: {},
  dedicationType: null,
  developers: [],
  donationDate: new Date(),
  donationType: find(({ value }) => value === 'general', DONATION_TYPE_OPTIONS),
  donor: null,
  earnedRevenue: '$0',
  event: null,
  pledgeDonationId: undefined,
  showEarnedRevenue: false,
  softCredits: [],
  ticket: null,
};

export function getDateFromDonation({ donationDateString } = {}) {
  return donationDateString
    ? dateStringToDate(donationDateString)
    : DEFAULT_VALUES.donationDate;
}

export function getTypeFromDonation(donation) {
  const found = find(
    ({ value }) => value === donation.donationTypeEnum,
    DONATION_TYPE_OPTIONS,
  );

  return found || DEFAULT_VALUES.donationType;
}

export function formatTeamMemberForSelect(teamMember) {
  return {
    value: teamMember.userId,
    icon: '',
    text: `@${teamMember.displayName}`,
    isAvatar: true,
    donorType: DonorTypes.USER,
  };
}

export function getCampaignFromDonation(donation, campaign) {
  if (!donation?.campaignId && !campaign) {
    return null;
  }

  if (donation?.campaignId) {
    return {
      text: donation.campaignDisplayName,
      value: donation.campaignId,
    };
  }

  return {
    text: campaign.displayName,
    value: campaign.campaignId,
  };
}

export function getEventFromDonation(donation) {
  if (!donation?.ticketedEventId) {
    return null;
  }

  return {
    text: donation.ticketedEventName,
    value: donation.ticketedEventId,
  };
}

export function getTicketFromDonation(donation) {
  if (!donation?.ticketId) {
    return null;
  }

  return {
    text: donation.ticketName,
    value: donation.ticketId,
  };
}

export function getDonorFromDonation(donation) {
  if (!donation?.constituents?.length) {
    return null;
  }

  return donation.constituents.map(
    ({ constituentId, constituentType, displayName }) => {
      return {
        constituentType,
        icon: '',
        isAvatar: true,
        text: displayName,
        value: constituentId,
      };
    },
  )[0];
}

export function getDefaultValues({ campaign, donation = {} } = {}) {
  return {
    ...DEFAULT_VALUES,
    attachToPledge: !!donation?.pledgeDonationId,
    attachments: (donation?.attachments || []).map(({ uploadId }) => uploadId),
    campaign: getCampaignFromDonation(donation, campaign),
    donationDate: getDateFromDonation(donation),
    developers: donation?.developers?.map(formatTeamMemberForSelect) || [],
    donationType: getTypeFromDonation(donation),
    donor: getDonorFromDonation(donation),
    event: getEventFromDonation(donation),
    earnedRevenue: formatCurrency(donation?.earnedRevenue || 0, true),
    pledgeDonationId: donation?.pledgeDonationId,
    showEarnedRevenue: !!donation?.earnedRevenue,
    ticket: getTicketFromDonation(donation),
  };
}

export function formatCampaignForAPI({ campaign, ...form }) {
  if (!campaign)
    return { ...form, campaignDisplayName: null, campaignId: null };

  return {
    ...form,
    campaignDisplayName: campaign.text,
    campaignId: campaign.value,
  };
}

export function formatEventForAPI({ event, ...form }) {
  if (!event) return { ...form, ticketedEventId: null };

  return {
    ...form,
    ticketedEventName: event.text,
    ticketedEventId: event.value,
  };
}

export function formatDonationDateForAPI({ donationDate, ...form }) {
  return {
    ...form,
    donationDateString: formatDateObject(donationDate, API_DATE_FORMAT),
  };
}

export function formatDonationTypeForAPI({ donationType, ...form }) {
  return {
    ...form,
    donationTypeEnum: donationType.value,
  };
}

export function formatDevelopersForAPI({ developers, ...form }) {
  if (developers.length === 0) return form;
  return {
    ...form,
    developers: developers?.map(({ text, value }) => {
      return {
        displayName: text.replace(/^@/, ''),
        userId: value,
      };
    }),
  };
}

export function formatEarnedRevenue({ earnedRevenue, ...form }) {
  return canHaveEarnedRevenue(form.donationTypeEnum)
    ? {
        ...form,
        earnedRevenue: formatCurrencyInputToCents(earnedRevenue),
      }
    : form;
}

export function formatSoftCredits({ softCredits, ...form }) {
  return {
    ...form,
    softCreditIds: Array.isArray(softCredits)
      ? softCredits.map(({ constituentId }) => constituentId)
      : [],
  };
}

export function formatDedication({
  dedication,
  dedicatedTo,
  dedicationType,
  ...form
}) {
  return dedicatedTo
    ? {
        ...form,
        dedication: {
          type: dedicationType,
          dedicationMessage: '', // Field is not currently used, but will be.
          dedicatedTo: dedicatedTo.displayName,
          ...(dedicatedTo.constituentId && {
            constituentId: dedicatedTo.constituentId,
          }),
        },
      }
    : { ...form, dedication: null };
}

export function formatTickets({
  ticket,
  ticketFmv,
  ticketId,
  ticketQuantity,
  ...form
}) {
  return form.donationTypeEnum === ContributionType.TICKET
    ? {
        ...form,
        ticketId: ticketId || ticket?.value || null,
        ticketName: ticket?.text || null,
        ticketFmv: formatCurrencyInputToCents(ticketFmv),
        ticketQuantity: parseInt(ticketQuantity, 10),
      }
    : form;
}

export function formatDataForAPI(form) {
  return flow(
    cond([
      [has('developers'), formatDevelopersForAPI],
      [T, identity],
    ]),
    formatCampaignForAPI,
    formatConstituent,
    formatDonationDateForAPI,
    formatDonationTypeForAPI,
    formatEarnedRevenue,
    formatEventForAPI,
    formatSoftCredits,
    formatDedication,
    formatTickets,
    omit([
      'attachToPledge',
      'pledgeDonationId',
      'dedicatedTo',
      'dedicationType',
    ]),
  )(form);
}
